/**
 * ElevenLabs Streaming TTS Service
 *
 * Manages a persistent WebSocket connection to ElevenLabs multi-context
 * stream-input endpoint for real-time text-to-speech with minimal latency.
 *
 * Key features:
 * - Multi-context WebSocket: single persistent connection across response turns
 * - Context-level interruption (no reconnect needed between responses)
 * - Generation timing via chunk_length_schedule + flush hints (optional auto_mode)
 * - Reconnection with exponential backoff
 * - Keepalive to prevent inactivity timeout
 * - Configurable voice settings (stability, speed, etc.)
 * - Support for global endpoint (lower latency)
 */

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error'

export type StreamingOutputFormat =
  | 'mp3_22050_32'
  | 'mp3_44100_64'
  | 'mp3_44100_96'
  | 'mp3_44100_128'
  | 'mp3_44100_192'
  | 'pcm_16000'
  | 'pcm_22050'
  | 'pcm_24000'
  | 'pcm_44100'
  | 'ulaw_8000'

export interface StreamingVoiceSettings {
  /** Voice stability (0.0-1.0). Lower = more expressive, higher = more consistent. Default: 0.5 */
  stability?: number
  /** Similarity boost (0.0-1.0). Higher = closer to original voice. Default: 0.75 */
  similarityBoost?: number
  /** Speaking speed (0.7-1.2). Default: 1.0 */
  speed?: number
  /** Speaker boost for V2+ models. Default: true */
  useSpeakerBoost?: boolean
  /** Style exaggeration (0.0-1.0) for V2+ models. */
  style?: number
}

/** Pronunciation dictionary locator for TTS calls */
export interface PronunciationDictionaryLocator {
  pronunciation_dictionary_id: string
  version_id: string
}

export interface StreamingTTSConfig {
  voiceId: string
  apiKey: string
  modelId?: string
  outputFormat?: StreamingOutputFormat
  useGlobalEndpoint?: boolean
  inactivityTimeoutSeconds?: number
  /** Voice tuning settings */
  voiceSettings?: StreamingVoiceSettings
  /** Pronunciation dictionary locators to apply during TTS */
  pronunciationDictionaryLocators?: PronunciationDictionaryLocator[]
  /**
   * ElevenLabs auto_mode: synthesize whenever the incoming text stream
   * stalls. Documented for full-sentence sends only; with phrase-trickled
   * LLM streams it fragments generation into tiny windows and pronunciation
   * dictionary matching becomes unreliable. Default false — generation is
   * driven by chunk_length_schedule plus our sentence-end flush hints.
   */
  autoMode?: boolean
}

export interface StreamingTTSEvents {
  onAudioChunk: (audioData: ArrayBuffer, format: StreamingOutputFormat) => void
  onStateChange: (state: ConnectionState) => void
  onError: (error: Error) => void
  onComplete: () => void
}

interface ElevenLabsAudioMessage {
  audio?: string // Base64 encoded audio
  isFinal?: boolean | null
  /** Multi-context: the context this message belongs to (null for the default context). */
  contextId?: string | null
  normalizedAlignment?: {
    char_start_times_ms: number[]
    chars_durations_ms: number[]
    chars: string[]
  }
}

const ELEVENLABS_WS_BASE = 'wss://api.elevenlabs.io/v1/text-to-speech'
const ELEVENLABS_GLOBAL_WS_BASE = 'wss://api-global-preview.elevenlabs.io/v1/text-to-speech'

/** Model used when the persona doesn't configure voice_config.model_id. */
export const DEFAULT_STREAMING_MODEL_ID = 'eleven_flash_v2_5'

/** Keepalive interval: send space to prevent inactivity timeout (15s < 30s default timeout) */
const KEEPALIVE_INTERVAL_MS = 15_000

/**
 * How long a closed context may stay silent before it is ended as if its
 * `isFinal` had come: a closed context does not time out, so a lost
 * `isFinal` would otherwise hang the turn (SA-1438)
 */
export const FINAL_WAIT_MS = 10_000

export class ElevenLabsStreamingService {
  private ws: WebSocket | null = null
  private state: ConnectionState = 'disconnected'
  private reconnectAttempts = 0
  private maxReconnectAttempts = 3
  private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null
  private config: StreamingTTSConfig
  private events: StreamingTTSEvents
  private isDisconnecting = false

  // Multi-context management
  private currentContextId: string | null = null
  /** True once close_context has been sent for the current context (it is closed at most once). */
  private currentContextClosed = false
  private contextCounter = 0
  private keepaliveIntervalId: ReturnType<typeof setInterval> | null = null
  private finalWatchdogId: ReturnType<typeof setTimeout> | null = null

  constructor(config: StreamingTTSConfig, events: StreamingTTSEvents) {
    this.config = {
      outputFormat: 'pcm_22050',
      useGlobalEndpoint: true,
      inactivityTimeoutSeconds: 30,
      ...config,
      // Nullish-guarded (not spread defaults): callers pass `modelId:
      // undefined` / `autoMode: undefined` when unset, which would clobber
      // a plain spread default.
      modelId: config.modelId ?? DEFAULT_STREAMING_MODEL_ID,
      autoMode: config.autoMode ?? false,
    }
    this.events = events
  }

  /**
   * Get the configured voice ID for this service instance.
   */
  getVoiceId(): string {
    return this.config.voiceId
  }

  /**
   * Get the configured pronunciation dictionary locators.
   */
  getPronunciationDictionaryLocators(): PronunciationDictionaryLocator[] | undefined {
    return this.config.pronunciationDictionaryLocators
  }

  /**
   * Get the resolved model ID (default applied) for this service instance.
   */
  getModelId(): string {
    return this.config.modelId!
  }

  /**
   * Get the resolved auto_mode (default applied) for this service instance.
   */
  getAutoMode(): boolean {
    return this.config.autoMode!
  }

  /**
   * Get current connection state
   */
  getState(): ConnectionState {
    return this.state
  }

  /**
   * Get current context ID
   */
  getCurrentContextId(): string | null {
    return this.currentContextId
  }

  /**
   * Whether the socket is OPEN. While a close the server started is under
   * way it is CLOSING and the state still reads `connected` (SA-1438).
   */
  isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN
  }

  /**
   * Whether a reconnect is scheduled after an unexpected close. True already
   * when `disconnected` is reported for that close (SA-1438).
   */
  isReconnectScheduled(): boolean {
    return this.reconnectTimeoutId !== null
  }

  /**
   * Update configuration (requires reconnection to take effect for connection-level settings)
   */
  updateConfig(updates: Partial<StreamingTTSConfig>): void {
    this.config = { ...this.config, ...updates }
  }

  /**
   * Start a new generation context on the existing WebSocket connection.
   * Closes the current context (if any) and returns a fresh context ID.
   *
   * Sends a context-init message with pronunciation dictionary locators
   * (if configured) so each context inherits the correct pronunciation settings.
   * Without this, only the default (unnamed) context from the connection init
   * message would have pronunciation locators applied.
   *
   * This is the primary mechanism for handling interruptions and new responses
   * without tearing down the WebSocket connection.
   *
   * @returns The new context ID
   */
  startNewContext(): string {
    // Close current context if one exists
    this.closeCurrentContext()

    // Generate new context ID
    this.contextCounter++
    this.currentContextId = `ctx_${Date.now()}_${this.contextCounter}`
    this.currentContextClosed = false

    this.sendContextInit(this.currentContextId)

    return this.currentContextId
  }

  /**
   * Send a context-init message to set pronunciation dictionaries for a context.
   * The connection-level init message only applies locators to the default context;
   * named contexts need their own initialization. A plain text message with an
   * unknown context_id creates the context implicitly WITHOUT locators, so this
   * must reach the socket before any text for the context.
   */
  private sendContextInit(contextId: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return
    }

    const contextInit: Record<string, unknown> = {
      text: ' ',
      context_id: contextId,
    }

    if (this.config.pronunciationDictionaryLocators?.length) {
      contextInit.pronunciation_dictionary_locators =
        this.config.pronunciationDictionaryLocators
    }

    try {
      this.ws.send(JSON.stringify(contextInit))
    } catch (error) {
      console.error('[ElevenLabs Streaming] Failed to send context init:', error)
    }
  }

  /**
   * Abandon the current context without closing the WebSocket.
   * Sends `close_context` alone (no flush): the server drops text it has not
   * yet flushed. A context already closed by finalizeContext() is not closed
   * again. The current context becomes null.
   */
  closeCurrentContext(): void {
    const contextId = this.currentContextId
    if (
      contextId &&
      !this.currentContextClosed &&
      this.ws &&
      this.ws.readyState === WebSocket.OPEN
    ) {
      try {
        this.ws.send(JSON.stringify({ context_id: contextId, close_context: true }))
      } catch (error) {
        console.error('[ElevenLabs Streaming] Failed to close context:', error)
      }
      console.debug('[ElevenLabs Streaming] Closed context:', contextId)
    }
    this.stopFinalWatchdog()
    this.currentContextId = null
    this.currentContextClosed = false
  }

  /**
   * Interrupt the current context.
   * Closes the current context (discards any pending audio for it)
   * but keeps the WebSocket connection alive for the next response.
   */
  interruptContext(): void {
    if (this.currentContextId) {
      const closedId = this.currentContextId
      this.closeCurrentContext()
      console.debug(
        '[ElevenLabs Streaming] Interrupted context:',
        closedId
      )
    }
  }

  /**
   * Finalize the current context.
   * Sends `flush` (generate what is buffered) then `close_context`, in that
   * order. An empty text frame is only a keep-alive and `close_context` alone
   * drops unflushed text, so both frames are needed. The context stays current
   * so its audio and `isFinal` are still accepted; a second call sends nothing.
   * The WebSocket stays open for future responses.
   */
  finalizeContext(): void {
    const contextId = this.currentContextId
    if (
      !contextId ||
      this.currentContextClosed ||
      !this.ws ||
      this.ws.readyState !== WebSocket.OPEN
    ) {
      return
    }

    try {
      this.ws.send(JSON.stringify({ context_id: contextId, flush: true }))
      this.ws.send(JSON.stringify({ context_id: contextId, close_context: true }))
      this.currentContextClosed = true
      this.armFinalWatchdog()
    } catch (error) {
      console.error('[ElevenLabs Streaming] Failed to finalize context:', error)
    }

    console.debug('[ElevenLabs Streaming] Finalized context:', contextId)
  }

  /**
   * Connect to ElevenLabs multi-context WebSocket.
   * Returns a Promise that resolves when the WebSocket is actually connected.
   *
   * The connection is persistent — it stays open across multiple response turns.
   * Use startNewContext() to begin a new response, not connect().
   */
  async connect(): Promise<void> {
    // Guard against empty voiceId (would produce an invalid WebSocket URL)
    if (!this.config.voiceId) {
      const error = new Error('Cannot connect: voiceId is not configured')
      this.setState('error')
      this.events.onError(error)
      return Promise.reject(error)
    }

    // If already connected, resolve immediately
    if (this.state === 'connected') {
      return
    }

    // If already connecting, wait for that connection attempt
    if (this.state === 'connecting') {
      return this.waitForConnection()
    }

    // Clear any pending reconnect
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId)
      this.reconnectTimeoutId = null
    }

    // A closed context's wait belongs to the old socket (SA-1438)
    this.stopFinalWatchdog()

    // Close any existing WebSocket before creating a new one
    if (this.ws) {
      this.ws.onopen = null
      this.ws.onclose = null
      this.ws.onerror = null
      this.ws.onmessage = null
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close()
      }
      this.ws = null
    }

    this.setState('connecting')
    this.isDisconnecting = false

    const baseUrl = this.config.useGlobalEndpoint
      ? ELEVENLABS_GLOBAL_WS_BASE
      : ELEVENLABS_WS_BASE

    // Build WebSocket URL with query parameters
    // Note: API key is sent in the initial message, not query params (more reliable for browsers)
    const params = new URLSearchParams({
      model_id: this.config.modelId!,
      output_format: this.config.outputFormat!,
      auto_mode: String(this.config.autoMode),
      inactivity_timeout: this.config.inactivityTimeoutSeconds!.toString(),
    })

    // Use multi-context endpoint for persistent connection with context management
    const wsUrl = `${baseUrl}/${this.config.voiceId}/multi-stream-input?${params}`

    return new Promise((resolve, reject) => {
      try {
        const ws = new WebSocket(wsUrl)
        ws.binaryType = 'arraybuffer'
        this.ws = ws

        // Capture ws reference in closure to avoid issues with this.ws being replaced
        ws.onopen = () => {
          // Verify this is still the current WebSocket
          if (this.ws !== ws) {
            console.warn('[ElevenLabs Streaming] WebSocket replaced before onopen')
            ws.close()
            reject(new Error('WebSocket replaced before connection'))
            return
          }

          // Send initial message with API key, voice settings, generation config,
          // and pronunciation dictionary locators
          const voiceSettings = this.config.voiceSettings ?? {}
          const initMessage: Record<string, unknown> = {
            text: ' ', // Initial space as per ElevenLabs docs
            xi_api_key: this.config.apiKey,
            voice_settings: {
              stability: voiceSettings.stability ?? 0.5,
              similarity_boost: voiceSettings.similarityBoost ?? 0.75,
              use_speaker_boost: voiceSettings.useSpeakerBoost ?? true,
              ...(voiceSettings.speed != null ? { speed: voiceSettings.speed } : {}),
              ...(voiceSettings.style != null ? { style: voiceSettings.style } : {}),
            },
            generation_config: {
              // ElevenLabs' recommended default schedule for quality-latency balance:
              // First generation at 120 chars (fast TTFB), later ones larger (more context)
              chunk_length_schedule: [120, 160, 250, 290],
            },
          }

          // Include pronunciation dictionary locators if configured
          if (this.config.pronunciationDictionaryLocators?.length) {
            initMessage.pronunciation_dictionary_locators =
              this.config.pronunciationDictionaryLocators
          }
          ws.send(JSON.stringify(initMessage))

          // A context that survives an auto-reconnect exists only on the old
          // socket — re-initialize it here so queued text drained onto this
          // connection doesn't implicitly recreate it without locators.
          // One already closed is forgotten: it ended on the old socket.
          if (this.currentContextId) {
            if (this.currentContextClosed) {
              this.stopFinalWatchdog()
              this.currentContextId = null
              this.currentContextClosed = false
            } else {
              this.sendContextInit(this.currentContextId)
            }
          }

          // Start keepalive to prevent inactivity timeout
          this.startKeepalive()

          // Report connected only once the init is on the socket: no frame
          // may go out before it (the server refuses and closes).
          this.setState('connected')
          this.reconnectAttempts = 0
          resolve()
        }

        ws.onmessage = (event) => {
          // Ignore messages from replaced WebSocket
          if (this.ws !== ws) return
          this.handleMessage(event)
        }

        ws.onerror = (event) => {
          // Ignore errors from replaced WebSocket
          if (this.ws !== ws) return
          console.error('[ElevenLabs Streaming] WebSocket error:', event)
          this.setState('error')
          const error = new Error('WebSocket connection error')
          this.events.onError(error)
          reject(error)
        }

        ws.onclose = (event) => {
          // Ignore close events from replaced WebSocket
          if (this.ws !== ws) return
          console.debug('[ElevenLabs Streaming] WebSocket closed:', event.code, event.reason)

          // Stop keepalive
          this.stopKeepalive()
          // The disconnected rule ends a reply waiting on this socket (SA-1438)
          this.stopFinalWatchdog()

          if (this.state === 'error') {
            // Already in error state, don't change - user must manually retry
            return
          }

          // Code 1006 = abnormal closure (connection failed, e.g., "Insufficient resources")
          // Don't attempt to reconnect for connection failures
          if (event.code === 1006) {
            this.setState('error')
            const error = new Error(`WebSocket connection failed: ${event.reason || 'Abnormal closure (1006)'}`)
            this.events.onError(error)
            reject(error)
            return
          }

          if (!this.isDisconnecting) {
            // Unexpected close, try to reconnect
            this.scheduleReconnect()
          } else {
            this.setState('disconnected')
          }
        }
      } catch (error) {
        this.setState('error')
        this.events.onError(
          error instanceof Error ? error : new Error('Failed to create WebSocket')
        )
        reject(error)
      }
    })
  }

  /**
   * Wait for an in-progress connection to complete
   */
  private waitForConnection(): Promise<void> {
    return new Promise((resolve, reject) => {
      const checkConnection = () => {
        if (this.state === 'connected') {
          resolve()
        } else if (this.state === 'error' || this.state === 'disconnected') {
          reject(new Error('Connection failed'))
        } else {
          // Still connecting, check again in 50ms
          setTimeout(checkConnection, 50)
        }
      }
      checkConnection()
    })
  }

  /**
   * Disconnect from WebSocket entirely.
   * Only call this on unmount or when disabling TTS.
   * For interruptions between responses, use interruptContext() instead.
   */
  async disconnect(): Promise<void> {
    this.isDisconnecting = true
    this.currentContextId = null
    this.currentContextClosed = false

    this.stopKeepalive()
    this.stopFinalWatchdog()

    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId)
      this.reconnectTimeoutId = null
    }

    if (this.ws) {
      // Send close message
      if (this.ws.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(JSON.stringify({ close_socket: true }))
        } catch {
          // Ignore send errors during close
        }
      }
      this.ws.close()
      this.ws = null
    }

    this.setState('disconnected')
    this.reconnectAttempts = 0
  }

  /**
   * Send text chunk to be synthesized.
   * Text should end with a space for best results.
   *
   * In multi-context mode, text is tagged with the current context ID.
   * Generation timing: chunk_length_schedule + flush hints (default), or
   * auto_mode when configured — no try_trigger_generation needed either way.
   *
   * @param text - Text to synthesize (should end with space)
   * @param flush - Force generation of buffered audio (use at sentence boundaries for prosody)
   */
  sendText(text: string, flush = false): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('[ElevenLabs Streaming] Cannot send text: not connected')
      return
    }

    if (!text && !flush) {
      return
    }

    const message: Record<string, unknown> = {
      text: text,
    }

    // Tag with current context for multi-context routing
    if (this.currentContextId) {
      message.context_id = this.currentContextId
    }

    if (flush) {
      message.flush = true
    }

    try {
      this.ws.send(JSON.stringify(message))
    } catch (error) {
      console.error('[ElevenLabs Streaming] Failed to send text:', error)
      this.events.onError(
        error instanceof Error ? error : new Error('Failed to send text')
      )
    }
  }

  /**
   * Handle incoming WebSocket message.
   * Filters audio by context ID to discard audio from interrupted/closed contexts.
   */
  private handleMessage(event: MessageEvent): void {
    try {
      if (typeof event.data === 'string') {
        const message: ElevenLabsAudioMessage = JSON.parse(event.data)

        // Any message, even one the filter drops (an interrupted context's
        // backlog), shows the server is still busy: the closed context's
        // isFinal may be queued behind it (SA-1438)
        if (this.finalWatchdogId) this.armFinalWatchdog()

        // Multi-context filter: only the current context is heard. Anything
        // else (another id, the default context's null, no id, or no context
        // current) is dropped whole, audio and isFinal alike.
        if (this.currentContextId === null || message.contextId !== this.currentContextId) {
          return
        }

        if (message.audio) {
          // Decode base64 audio to ArrayBuffer
          const binaryString = atob(message.audio)
          const bytes = new Uint8Array(binaryString.length)
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i)
          }
          this.events.onAudioChunk(bytes.buffer, this.config.outputFormat!)
        }

        if (message.isFinal) {
          this.stopFinalWatchdog()
          this.events.onComplete()
        }
      } else if (event.data instanceof ArrayBuffer) {
        // Binary audio data (alternative format)
        this.events.onAudioChunk(event.data, this.config.outputFormat!)
      }
    } catch (error) {
      console.error('[ElevenLabs Streaming] Failed to parse message:', error)
    }
  }

  /**
   * Update internal state and notify listeners
   */
  private setState(newState: ConnectionState): void {
    if (this.state !== newState) {
      this.state = newState
      this.events.onStateChange(newState)
    }
  }

  /**
   * Start keepalive timer to prevent inactivity timeout.
   * Sends a space character periodically to keep the WebSocket alive
   * between response turns.
   */
  private startKeepalive(): void {
    this.stopKeepalive()
    this.keepaliveIntervalId = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        try {
          // Send space to reset inactivity timer (per ElevenLabs docs)
          this.ws.send(JSON.stringify({ text: ' ' }))
          // An open context times out on its own after the inactivity
          // timeout without text: keep it alive while its reply has not
          // closed it, so a slow first word still speaks in it (SA-1438)
          if (this.currentContextId && !this.currentContextClosed) {
            this.ws.send(JSON.stringify({ context_id: this.currentContextId, text: '' }))
          }
        } catch {
          // Ignore keepalive send errors
        }
      }
    }, KEEPALIVE_INTERVAL_MS)
  }

  /**
   * (Re)start the wait for a closed context's `isFinal`. When it runs out the
   * context is dropped (no longer current, so a late `isFinal` or audio for it
   * is filtered) and the reply ends as `isFinal` would end it (SA-1438).
   */
  private armFinalWatchdog(): void {
    this.stopFinalWatchdog()
    const armedFor = this.currentContextId
    this.finalWatchdogId = setTimeout(() => {
      this.finalWatchdogId = null
      // Only the context it was armed for ends (SA-1438)
      if (this.currentContextId !== armedFor || !this.currentContextClosed) return
      console.warn(
        '[ElevenLabs Streaming] No isFinal after close; ending the context',
        this.currentContextId
      )
      this.currentContextId = null
      this.currentContextClosed = false
      this.events.onComplete()
    }, FINAL_WAIT_MS)
  }

  private stopFinalWatchdog(): void {
    if (this.finalWatchdogId) {
      clearTimeout(this.finalWatchdogId)
      this.finalWatchdogId = null
    }
  }

  /**
   * Stop keepalive timer
   */
  private stopKeepalive(): void {
    if (this.keepaliveIntervalId) {
      clearInterval(this.keepaliveIntervalId)
      this.keepaliveIntervalId = null
    }
  }

  /**
   * Schedule a reconnection attempt with exponential backoff
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[ElevenLabs Streaming] Max reconnection attempts reached')
      this.setState('error')
      this.events.onError(new Error('Max reconnection attempts reached'))
      return
    }

    this.reconnectAttempts++

    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 8000)
    console.debug(
      `[ElevenLabs Streaming] Scheduling reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`
    )

    // Armed before `disconnected` is reported, so a listener can tell a
    // reconnect is coming (SA-1438)
    this.reconnectTimeoutId = setTimeout(() => {
      this.reconnectTimeoutId = null
      this.connect()
    }, delay)
    this.setState('disconnected')
  }
}

/**
 * Get sample rate from output format
 */
export function getSampleRateForFormat(format: StreamingOutputFormat): number {
  const sampleRates: Record<StreamingOutputFormat, number> = {
    mp3_22050_32: 22050,
    mp3_44100_64: 44100,
    mp3_44100_96: 44100,
    mp3_44100_128: 44100,
    mp3_44100_192: 44100,
    pcm_16000: 16000,
    pcm_22050: 22050,
    pcm_24000: 24000,
    pcm_44100: 44100,
    ulaw_8000: 8000,
  }
  return sampleRates[format]
}

/**
 * Check if format is PCM (raw audio)
 */
export function isPCMFormat(format: StreamingOutputFormat): boolean {
  return format.startsWith('pcm_')
}

/**
 * Human-readable format names for UI
 */
export const OUTPUT_FORMAT_LABELS: Record<StreamingOutputFormat, string> = {
  mp3_22050_32: 'MP3 22kHz 32kbps (Low bandwidth)',
  mp3_44100_64: 'MP3 44kHz 64kbps',
  mp3_44100_96: 'MP3 44kHz 96kbps',
  mp3_44100_128: 'MP3 44kHz 128kbps (Recommended)',
  mp3_44100_192: 'MP3 44kHz 192kbps (High quality)',
  pcm_16000: 'PCM 16kHz (Lowest latency)',
  pcm_22050: 'PCM 22kHz (Low latency)',
  pcm_24000: 'PCM 24kHz',
  pcm_44100: 'PCM 44kHz',
  ulaw_8000: 'u-law 8kHz (Telephony)',
}
